import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { fileAccessRestrictedHref } from '../storageAccess/fileAccessRestrictedUrls';
import { DOCUMENT_HOST_OPEN_TIMEOUT_MS } from '../storageAccess/documentHostOpenFailed';
import {
  FileReviewWorkspace,
  type FileReviewPanelPlacement,
  enableFileReviewDiscussionPlacement,
  findAnchorById,
  readFileReviewPanelOpen,
  useAutoHideDiscussionPanel,
  useDiscussionAnchorFromUrl,
  useFileReviewPanelPlacement,
  useFileReviewPanelWidth,
  useFileReviewThread,
  writeFileReviewPanelOpen,
  writeFileReviewPanelPlacement
} from '@jarvis/platform-file-review';
import '@jarvis/platform-file-review/styles.css';
import { bootstrapFileReviewApi } from '../fileReview/bootstrapFileReviewApi';
import { usePdfViewerIdentity } from '../pdfViewer/usePdfViewerIdentity';
import { showConfirmDialog } from '../dialogs/confirmDialog';
import { showToast } from '../notifications/toast';
import { DiscussionNotificationPrompt } from '../notifications/DiscussionNotificationPrompt';
import { useDiscussionMessageNotifications } from '../notifications/useDiscussionMessageNotifications';
import { useMediaFileMetadata } from '../mediaViewer/mediaViewerMetadata';
import {
  formatMediaDateModified,
  formatMediaFileSize
} from '../mediaViewer/mediaViewerFormatters';
import { documentHostSrc, type DocumentEditorMode } from './documentEditorUrls';
import { syncThemeToFrame } from '../theme/themeManager';
import { useHtmlThemeMode } from '../theme/useHtmlThemeMode';
import { shareDocumentLink } from './documentViewerActions';
import {
  readDocumentTextLinksVisible,
  writeDocumentTextLinksVisible
} from './documentEditorUiPrefs';
import { DocumentCommentPopover } from './DocumentCommentPopover';
import { DocumentOpenPickerPopup } from './DocumentOpenPickerPopup';
import type { RichEditSelectionState } from './richEditBridgeTypes';
import { useDocumentEditorOpenFlow } from './useDocumentEditorOpenFlow';
import { useRichEditHostBridge } from './useRichEditHostBridge';
import { ActiveUsersBar } from '../realtime/ActiveUsersBar';
import { AutoSaveIndicator } from '../realtime/AutoSaveIndicator';
import { useDocumentPresence, peerEditLockToastMessage } from '../realtime/useDocumentPresence';
import { useDocumentEditingSignals } from '../realtime/useDocumentEditingSignals';
import { useDocumentFileSavedHandler } from '../editorPreferences/useDocumentFileSavedHandler';
import { MediaViewerTitle } from '../mediaViewer/MediaViewerTitle';
import '../mediaViewer/MediaViewerTitle.css';
import { EditorCollaborationReloadOverlay } from '../realtime/EditorCollaborationReloadOverlay';
import '../realtime/EditorCollaborationReload.css';
import '../realtime/EditorHeader.css';
import { isFileReviewDisabledStorageMount } from '../storageFileAudit/formatters';
import {
  isDocumentPresenceEnabled,
  shouldShowDocumentViewOnlyBadge
} from './documentEditorCollaboration';
import { useShellLayout } from '../shell/useShellLayout';
import './DocumentEditorPage.css';

bootstrapFileReviewApi();

export interface DocumentEditorShellProps {
  mode: DocumentEditorMode;
  mountId: string;
  path: string;
  title: string;
  readOnly?: boolean;
  discussionPlacement?: FileReviewPanelPlacement;
  /** Hide title row when nested inside the file-browser preview popup. */
  embeddedInPopup?: boolean;
}

export function DocumentEditorShell({
  mode,
  mountId,
  path,
  title,
  readOnly = false,
  discussionPlacement: discussionPlacementProp,
  embeddedInPopup = false
}: DocumentEditorShellProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isNarrow } = useShellLayout();
  const discussionAnchorParam = (searchParams.get('discussionAnchor') ?? '').trim();
  const discussionPlacement = useFileReviewPanelPlacement('word');
  const discussionsEnabled = !isFileReviewDisabledStorageMount(mountId) && !isNarrow;
  const placement = discussionsEnabled
    ? (discussionPlacementProp ?? discussionPlacement)
    : 'off';
  const autoHideDiscussion = useAutoHideDiscussionPanel();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const navigatedToRestrictedRef = useRef(false);
  const [chatOpen, setChatOpen] = useState(() => readFileReviewPanelOpen('word'));
  const [showTextLinks, setShowTextLinks] = useState(readDocumentTextLinksVisible);
  const [activeAnchorId, setActiveAnchorId] = useState<string | null>(null);
  const [commentPopoverSelection, setCommentPopoverSelection] =
    useState<RichEditSelectionState | null>(null);
  const [newerVersionBy, setNewerVersionBy] = useState<string | null>(null);
  const [peerReloading, setPeerReloading] = useState(false);
  const htmlTheme = useHtmlThemeMode();

  const goToAccessRestricted = useCallback(() => {
    if (navigatedToRestrictedRef.current) return;
    navigatedToRestrictedRef.current = true;
    navigate(fileAccessRestrictedHref(), { replace: true });
  }, [navigate]);

  const src = useMemo(
    () => documentHostSrc('richedit', mode, mountId, path, htmlTheme, discussionsEnabled),
    [mode, mountId, path, htmlTheme, discussionsEnabled]
  );

  const metadata = useMediaFileMetadata(mountId, path, true);
  const { userId, userName, hasPhoto, userAliases } = usePdfViewerIdentity(true);
  const { panelStyle, startResize, resetWidth } = useFileReviewPanelWidth(
    workspaceRef,
    placement,
    'word'
  );

  const onReviewError = useCallback((message: string) => showToast(message, 'error'), []);

  const openDiscussionPanel = useCallback(() => {
    if (!discussionsEnabled) return;
    if (placement === 'off') {
      enableFileReviewDiscussionPlacement('word');
    }
    setChatOpen(true);
    writeFileReviewPanelOpen(true, 'word');
  }, [discussionsEnabled, placement]);

  const { onPeerActivity } = useDiscussionMessageNotifications({
    enabled: discussionsEnabled && Boolean(mountId && path && userId),
    onOpenDiscussionPanel: openDiscussionPanel
  });

  const review = useFileReviewThread(mountId, path, discussionsEnabled, {
    commentsPanelOpen: discussionsEnabled && placement !== 'off',
    onError: onReviewError,
    currentUserId: userId,
    currentUserAliases: userAliases,
    onPeerActivity
  });

  const toggleDiscussion = useCallback(() => {
    if (!discussionsEnabled) return;
    if (placement === 'off') {
      enableFileReviewDiscussionPlacement('word');
      setChatOpen(true);
      writeFileReviewPanelOpen(true, 'word');
      return;
    }
    setChatOpen((open) => {
      const next = !open;
      writeFileReviewPanelOpen(next, 'word');
      return next;
    });
  }, [discussionsEnabled, placement]);

  useDiscussionAnchorFromUrl({
    discussionAnchorId: discussionsEnabled ? discussionAnchorParam || null : null,
    onAnchorFromUrl: setActiveAnchorId,
    onOpenDiscussion: openDiscussionPanel
  });

  const hostMenuActionsRef = useRef<{
    commentOnSelection?: () => void;
    toggleTextLinks?: () => void;
    toggleDiscussion?: () => void;
    share?: () => void;
    open?: (source: 'pc' | 'storage') => void;
    setDiscussionPlacement?: (placement: FileReviewPanelPlacement) => void;
  }>({});
  const bridgeSelectionRef = useRef<RichEditSelectionState | null>(null);

  const onDiscussionPlacementChange = useCallback(
    (next: FileReviewPanelPlacement) => {
      if (!discussionsEnabled) return;
      const open = next !== 'off';
      writeFileReviewPanelPlacement(next, 'word');
      writeFileReviewPanelOpen(open, 'word');
      setChatOpen(open);
      if (!open) setActiveAnchorId(null);
    },
    [discussionsEnabled]
  );

  const toggleTextLinks = useCallback(() => {
    setShowTextLinks((visible) => {
      const next = !visible;
      writeDocumentTextLinksVisible(next);
      showToast(
        next
          ? 'Text links on — click a discussion message to jump to its selection'
          : 'Text links off',
        'info'
      );
      return next;
    });
  }, []);

  const commentOnSelection = useCallback(() => {
    if (!discussionsEnabled) return;
    const sel = bridgeSelectionRef.current;
    if (!sel?.range || !sel.selectedText.trim()) {
      showToast('Select document text first.', 'warning');
      return;
    }
    setCommentPopoverSelection(sel);
  }, [discussionsEnabled]);

  const shareDocument = useCallback(() => {
    void shareDocumentLink('richedit', mode === 'preview' ? 'preview' : 'edit', mountId, path);
  }, [mode, mountId, path]);

  const {
    ready: bridgeReady,
    editorSessionId,
    selection: bridgeSelection,
    autoSave,
    navigateToAnchor,
    requestForceReload,
    applyCollaborationSaveTime,
    requestDirtyState,
    requestAutoSaveNow,
    setDiscussionState,
    setTextLinksState,
    setDiscussionPlacementState,
    setPeerEditLocked
  } = useRichEditHostBridge(iframeRef, true, {
    onNavigationFailed: () => showToast('Could not jump to that selection.', 'warning'),
    onOpenFailed: () => goToAccessRestricted(),
    onRequestCommentOnSelection: () => hostMenuActionsRef.current.commentOnSelection?.(),
    onRequestToggleTextLinks: () => hostMenuActionsRef.current.toggleTextLinks?.(),
    onRequestToggleDiscussion: () => hostMenuActionsRef.current.toggleDiscussion?.(),
    onRequestShare: () => hostMenuActionsRef.current.share?.(),
    onRequestOpen: (source) => hostMenuActionsRef.current.open?.(source),
    onRequestSetDiscussionPlacement: (next) =>
      hostMenuActionsRef.current.setDiscussionPlacement?.(next)
  });

  const {
    pickerOpen,
    setPickerOpen,
    fileInputRef,
    fileInputAccept,
    handleOpenRequest,
    onFileInputChange,
    onPickerOpen
  } = useDocumentEditorOpenFlow({
    kind: 'richedit',
    mountId,
    path,
    requestDirtyState
  });

  useEffect(() => {
    navigatedToRestrictedRef.current = false;
  }, [mountId, path]);

  useEffect(() => {
    // Popup previews should not navigate the whole SPA away on a slow open.
    if (embeddedInPopup || bridgeReady || peerReloading) return;
    const timer = window.setTimeout(() => goToAccessRestricted(), DOCUMENT_HOST_OPEN_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [embeddedInPopup, bridgeReady, peerReloading, goToAccessRestricted, mountId, path]);

  bridgeSelectionRef.current = bridgeSelection;
  hostMenuActionsRef.current = {
    commentOnSelection,
    toggleTextLinks,
    toggleDiscussion,
    share: shareDocument,
    open: handleOpenRequest,
    setDiscussionPlacement: onDiscussionPlacementChange
  };

  useEffect(() => {
    setActiveAnchorId(null);
    setCommentPopoverSelection(null);
  }, [mountId, path]);

  useEffect(() => {
    if (!bridgeReady) return;
    setDiscussionState(discussionsEnabled && chatOpen && placement !== 'off');
  }, [bridgeReady, chatOpen, discussionsEnabled, placement, setDiscussionState]);

  useEffect(() => {
    if (!bridgeReady) return;
    setTextLinksState(showTextLinks);
  }, [bridgeReady, showTextLinks, setTextLinksState]);

  useEffect(() => {
    if (!bridgeReady) return;
    setDiscussionPlacementState(placement);
  }, [bridgeReady, placement, setDiscussionPlacementState]);

  const cancelCommentPopover = useCallback(() => {
    setCommentPopoverSelection(null);
  }, []);

  const saveCommentPopover = useCallback(
    async (comment: string) => {
      const sel = commentPopoverSelection;
      if (!sel?.range) return;
      const anchorId = await review.commitWordAnchor({
        range: sel.range,
        selectedText: sel.selectedText,
        comment
      });
      setCommentPopoverSelection(null);
      if (anchorId) {
        setActiveAnchorId(anchorId);
        openDiscussionPanel();
        if (showTextLinks && bridgeReady) {
          navigateToAnchor({
            id: anchorId,
            kind: 'word',
            selectedText: sel.selectedText,
            wordRange: sel.range,
            createdBy: '',
            createdByDisplayName: null,
            sourceSite: '',
            createdAt: new Date().toISOString()
          });
        }
      }
    },
    [bridgeReady, commentPopoverSelection, navigateToAnchor, openDiscussionPanel, review, showTextLinks]
  );

  const onMessageEntered = useCallback(
    async (text: string) => {
      // Normal discussion messages are unpinned. Text-linked comments are created only through the
      // explicit "comment on selection" flow; activeAnchorId is just for highlighting/jump state.
      await review.sendMessage(text, null);
    },
    [review]
  );

  const onMessageClick = useCallback(
    (anchorId: string | null) => {
      setActiveAnchorId(anchorId);
      if (!anchorId) return;
      if (!showTextLinks) {
        showToast('Turn on text links to jump to selections in the document', 'info');
        return;
      }
      if (!bridgeReady) {
        showToast('Document is still loading — try again in a moment', 'warning');
        return;
      }
      const anchor = findAnchorById(review.anchors, anchorId);
      if (!anchor || anchor.kind !== 'word' || !anchor.wordRange) {
        showToast('No text range for this message', 'warning');
        return;
      }
      navigateToAnchor(anchor);
    },
    [bridgeReady, navigateToAnchor, review.anchors, showTextLinks]
  );

  const onMessageDeleted = useCallback(
    async (messageId: string) => {
      // Deleting the last message on a selection comment also removes its
      // anchor server-side (ITB-1649); drop the stale active-anchor reference.
      const deletedAnchorId = review.messages.find((m) => m.id === messageId)?.anchorId ?? null;
      await review.deleteMessage(messageId);
      if (deletedAnchorId) {
        setActiveAnchorId((current) => (current === deletedAnchorId ? null : current));
      }
    },
    [review]
  );

  useEffect(() => {
    if (bridgeReady && peerReloading) setPeerReloading(false);
  }, [bridgeReady, peerReloading]);

  useEffect(() => {
    if (!peerReloading) return;
    const timer = window.setTimeout(() => setPeerReloading(false), 60000);
    return () => window.clearTimeout(timer);
  }, [peerReloading]);

  const peerEditLockedRef = useRef(false);
  const onFileSaved = useDocumentFileSavedHandler({
    userAliases,
    userDisplayName: userName,
    editorSessionId,
    requestDirtyState,
    requestForceReload,
    onCollaborationSaveApplied: applyCollaborationSaveTime,
    setNewerVersionBy,
    onPeerReloadStarting: () => setPeerReloading(true),
    isPeerEditLocked: () => peerEditLockedRef.current
  });

  const documentReadOnly = readOnly || mode === 'preview';
  const presenceEnabled = isDocumentPresenceEnabled({ mountId, embeddedInPopup });
  const [stoppingEditing, setStoppingEditing] = useState(false);

  const toastPeerEditLock = useCallback(
    (holder: import('../realtime/documentCollaborationTypes').DocumentEditLockHolder) => {
      showToast(peerEditLockToastMessage(holder), 'warning');
    },
    []
  );

  const {
    roster,
    notifyEditing,
    notifyTyping,
    isEditLockHolder,
    isPeerEditLocked,
    editLockHolder
  } = useDocumentPresence({
    mountId,
    path,
    enabled: presenceEnabled,
    currentUserId: userId,
    currentUserAliases: userAliases,
    onFileSaved,
    onPeerEditLock: toastPeerEditLock,
    onEditLockDenied: (holder) => showToast(peerEditLockToastMessage(holder), 'warning')
  });
  peerEditLockedRef.current = isPeerEditLocked;

  useEffect(() => {
    if (!bridgeReady || documentReadOnly) return;
    setPeerEditLocked(isPeerEditLocked);
  }, [bridgeReady, documentReadOnly, isPeerEditLocked, setPeerEditLocked]);

  useEffect(() => {
    if (!presenceEnabled || documentReadOnly) return;
    function onMessage(event: MessageEvent) {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || !('type' in data)) return;
      if (data.type === 'jarvis-richedit:peer-edit-blocked') {
        showToast(peerEditLockToastMessage(editLockHolder), 'warning');
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [documentReadOnly, editLockHolder, presenceEnabled]);

  const { isLocalTyping } = useDocumentEditingSignals({
    enabled: presenceEnabled && !documentReadOnly,
    iframeRef,
    editingChangedType: 'jarvis-richedit:editing-changed',
    typingActivityType: 'jarvis-richedit:typing-activity',
    notifyEditing,
    notifyTyping,
    peerEditLocked: isPeerEditLocked,
    onEditLockDeniedLocally: () => showToast(peerEditLockToastMessage(editLockHolder), 'warning')
  });

  const onStopEditing = useCallback(() => {
    if (documentReadOnly || stoppingEditing) return;
    setStoppingEditing(true);
    void (async () => {
      try {
        const dirty = await requestDirtyState();
        if (dirty) {
          const save = await requestAutoSaveNow();
          if (!save.ok) {
            const detail =
              save.reason === 'conflict'
                ? 'A newer version exists. Refresh it, then try again.'
                : 'Fix the save error, then try again.';
            showToast(`Could not stop editing. ${detail}`, 'error');
            return;
          }
        }
        const released = await notifyEditing(false);
        if (!released) {
          showToast('Could not release editing. Check your connection and try again.', 'error');
          return;
        }
        showToast(dirty ? 'Editing stopped. File saved.' : 'Editing stopped.', dirty ? 'success' : 'info');
      } finally {
        setStoppingEditing(false);
      }
    })();
  }, [documentReadOnly, notifyEditing, requestAutoSaveNow, requestDirtyState, stoppingEditing]);

  const onRefresh = useCallback(() => {
    void (async () => {
      const dirty = await requestDirtyState();
      if (dirty) {
        const ok = await showConfirmDialog({
          title: 'Discard unsaved changes?',
          message:
            'You have unsaved changes. Reloading the latest version will discard them. Continue?',
          confirmText: 'Reload',
          danger: true
        });
        if (!ok) return;
      }
      setNewerVersionBy(null);
      requestForceReload();
    })();
  }, [requestDirtyState, requestForceReload]);

  const annotateReadOnly = !discussionsEnabled || (!review.loading && !review.canAnnotate);
  const showViewOnlyBadge = shouldShowDocumentViewOnlyBadge({
    discussionsEnabled,
    annotateReadOnly,
    hasReviewError: Boolean(review.error)
  });

  return (
    <div
      className={
        embeddedInPopup
          ? 'document-editor-shell document-editor-shell--embedded'
          : 'document-editor-shell'
      }
    >
      {embeddedInPopup ? null : (
      <div className="editor-header">
        <MediaViewerTitle
          kind="word"
          title={title}
          path={path}
          placement={placement}
          discussionExpanded={chatOpen}
          onDiscussionToggle={discussionsEnabled ? toggleDiscussion : undefined}
          className="editor-header__title"
        />
        <span className="editor-header__center">
          {presenceEnabled ? (
            <ActiveUsersBar
              roster={roster}
              currentUserId={userId}
              maxAvatars={5}
              localTypingActive={isLocalTyping}
            />
          ) : null}
        </span>
        <span className="editor-header__meta" aria-label="File details">
          <span className="editor-header__meta-item">
            <span className="editor-header__meta-label">Modified</span>
            <span className="editor-header__meta-value">
              {formatMediaDateModified(metadata?.dateModified)}
            </span>
          </span>
          <span className="editor-header__meta-sep" aria-hidden="true">
            ·
          </span>
          <span className="editor-header__meta-item">
            <span className="editor-header__meta-label">Size</span>
            <span className="editor-header__meta-value">{formatMediaFileSize(metadata?.size)}</span>
          </span>
          {!documentReadOnly ? (
            <AutoSaveIndicator
              status={autoSave.status}
              lastSavedAt={autoSave.lastSavedAt}
              reason={autoSave.reason}
              onRetry={requestAutoSaveNow}
            />
          ) : null}
          {isEditLockHolder && !documentReadOnly ? (
            <button
              type="button"
              className="editor-header__stop-editing"
              title="Save and release editing so others can edit"
              aria-label="Stop editing"
              disabled={stoppingEditing}
              onClick={onStopEditing}
            >
              {stoppingEditing ? 'Saving…' : 'Stop Editing'}
            </button>
          ) : null}
          {isPeerEditLocked && !documentReadOnly ? (
            <span className="editor-header__badge" title={peerEditLockToastMessage(editLockHolder)}>
              Locked
            </span>
          ) : null}
          <button
            type="button"
            className="editor-header__refresh"
            title="Reload latest from storage"
            aria-label="Reload latest from storage"
            onClick={onRefresh}
          >
            <span className="dx-icon dx-icon-refresh" aria-hidden="true" />
          </button>
          {showViewOnlyBadge ? <span className="editor-header__badge">View only</span> : null}
          {discussionsEnabled && review.error && review.messages.length === 0 ? (
            <span className="editor-header__badge editor-header__badge--error" role="alert">
              Discussion unavailable
            </span>
          ) : null}
        </span>
      </div>
      )}
      {newerVersionBy ? (
        <div className="editor-newer-banner" role="alert">
          <span className="editor-newer-banner__msg">
            A newer version was saved by {newerVersionBy}. Refresh to load it (your unsaved edits will
            be discarded).
          </span>
          <button type="button" className="editor-newer-banner__action" onClick={onRefresh}>
            Refresh
          </button>
          <button
            type="button"
            className="editor-newer-banner__dismiss"
            aria-label="Dismiss"
            title="Dismiss"
            onClick={() => setNewerVersionBy(null)}
          >
            ×
          </button>
        </div>
      ) : null}
      <FileReviewWorkspace
        workspaceRef={workspaceRef}
        placement={placement}
        panelOpen={discussionsEnabled && chatOpen}
        autoHide={autoHideDiscussion}
        viewerClassName="document-editor-page__viewer"
        panelProps={{
          messages: review.messages,
          currentUserId: userId,
          currentUserAliases: userAliases,
          currentUserName: userName,
          currentUserHasPhoto: hasPhoto,
          loading: review.loading,
          readOnly: annotateReadOnly,
          canAnnotate: review.canAnnotate,
          activeAnchorId,
          jumpLabel: 'Jump to selection',
          title: 'Discussion',
          panelStyle: chatOpen ? panelStyle : undefined,
          onResizeStart: startResize,
          onResizeReset: resetWidth,
          onMessageEntered,
          onMessageUpdated: review.updateMessage,
          onMessageDeleted,
          onMessageClick,
          onComposeValidationError: onReviewError,
          mountId,
          path
        }}
      >
        <iframe
          ref={iframeRef}
          className="document-editor-page__frame"
          src={src}
          title={title}
          onLoad={(event) => syncThemeToFrame(event.currentTarget.contentWindow)}
        />
        <EditorCollaborationReloadOverlay
          visible={!bridgeReady || peerReloading}
          label={peerReloading ? 'Loading latest version…' : 'Loading document…'}
        />
        {discussionsEnabled && commentPopoverSelection ? (
          <DocumentCommentPopover
            mountId={mountId}
            path={path}
            onCancel={cancelCommentPopover}
            onSave={saveCommentPopover}
            onValidationError={onReviewError}
          />
        ) : null}
      </FileReviewWorkspace>
      <DiscussionNotificationPrompt enabled={discussionsEnabled && Boolean(mountId && path)} />
      <input
        ref={fileInputRef}
        type="file"
        accept={fileInputAccept}
        hidden
        onChange={(event) => void onFileInputChange(event)}
      />
      <DocumentOpenPickerPopup
        visible={pickerOpen}
        kind="richedit"
        onClose={() => setPickerOpen(false)}
        onOpen={onPickerOpen}
      />
    </div>
  );
}
